import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface AccessibilitySettings {
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  contrast: 'normal' | 'high';
  textToSpeech: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
  focusIndicator: boolean;
  dyslexiaFont: boolean;
  lineSpacing: 'normal' | 'increased';
  underlineLinks: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(
    key: K, 
    value: AccessibilitySettings[K]
  ) => void;
  resetSettings: () => void;
  isAccessibilityPanelOpen: boolean;
  toggleAccessibilityPanel: () => void;
  speakText: (text: string) => void;
  stopSpeaking: () => void;
  isSpeaking: boolean;
  currentSpeakingElement: string | null;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'medium',
  contrast: 'normal',
  textToSpeech: false,
  reducedMotion: false,
  screenReader: false,
  focusIndicator: true,
  dyslexiaFont: false,
  lineSpacing: 'normal',
  underlineLinks: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    // Încărcăm setările din localStorage
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      try {
        return { ...defaultSettings, ...JSON.parse(savedSettings) };
      } catch (error) {
        console.warn('Error loading accessibility settings:', error);
      }
    }
    return defaultSettings;
  });

  const [isAccessibilityPanelOpen, setIsAccessibilityPanelOpen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentSpeakingElement, setCurrentSpeakingElement] = useState<string | null>(null);

  // Referință pentru speech synthesis
  const speechRef = React.useRef<SpeechSynthesisUtterance | null>(null);

  // Aplicăm setările CSS la documentul întreg
  useEffect(() => {
    const applyAccessibilityStyles = () => {
      const root = document.documentElement;
      
      // Font size
      const fontSizes = {
        small: '14px',
        medium: '16px',
        large: '18px',
        'extra-large': '22px'
      };
      root.style.setProperty('--accessibility-font-size', fontSizes[settings.fontSize]);
      
      // Contrast
      if (settings.contrast === 'high') {
        root.classList.add('high-contrast');
      } else {
        root.classList.remove('high-contrast');
      }
      
      // Reduced motion
      if (settings.reducedMotion) {
        root.classList.add('reduced-motion');
      } else {
        root.classList.remove('reduced-motion');
      }
      
      // Focus indicator
      if (settings.focusIndicator) {
        root.classList.add('enhanced-focus');
      } else {
        root.classList.remove('enhanced-focus');
      }
      
      // Dyslexia font
      if (settings.dyslexiaFont) {
        root.classList.add('dyslexia-font');
      } else {
        root.classList.remove('dyslexia-font');
      }
      
      // Line spacing
      if (settings.lineSpacing === 'increased') {
        root.classList.add('increased-line-spacing');
      } else {
        root.classList.remove('increased-line-spacing');
      }
      
      // Underline links
      if (settings.underlineLinks) {
        root.classList.add('underline-links');
      } else {
        root.classList.remove('underline-links');
      }
    };

    applyAccessibilityStyles();
  }, [settings]);

  // Salvăm setările în localStorage
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  }, [settings]);

  // Cleanup speech synthesis la unmount
  useEffect(() => {
    return () => {
      if (speechRef.current) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  const updateSetting = useCallback(<K extends keyof AccessibilitySettings>(
    key: K, 
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(defaultSettings);
    localStorage.removeItem('accessibilitySettings');
  }, []);

  const toggleAccessibilityPanel = useCallback(() => {
    setIsAccessibilityPanelOpen(prev => !prev);
  }, []);

  const speakText = useCallback((text: string) => {
    if (!settings.textToSpeech || !text.trim()) return;

    // Oprire vorbire curentă
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    speechRef.current = utterance;

    // Configurare voce
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    // Event listeners
    utterance.onstart = () => {
      setIsSpeaking(true);
      setCurrentSpeakingElement(text.substring(0, 50) + '...');
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentSpeakingElement(null);
      speechRef.current = null;
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setCurrentSpeakingElement(null);
      speechRef.current = null;
    };

    // Încearcă să găsească o voce română
    const voices = speechSynthesis.getVoices();
    const romanianVoice = voices.find(voice => 
      voice.lang.startsWith('ro') || voice.name.toLowerCase().includes('romanian')
    );
    
    if (romanianVoice) {
      utterance.voice = romanianVoice;
    }

    speechSynthesis.speak(utterance);
  }, [settings.textToSpeech]);

  const stopSpeaking = useCallback(() => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentSpeakingElement(null);
    speechRef.current = null;
  }, []);

  // Auto-speak pentru screen readers când este activat
  useEffect(() => {
    if (!settings.textToSpeech) return;

    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target && target.textContent) {
        // Evităm să citim textul din input-uri
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          const label = target.getAttribute('aria-label') || target.getAttribute('placeholder') || target.getAttribute('title');
          if (label) {
            speakText(label);
          }
        } else {
          speakText(target.textContent);
        }
      }
    };

    // Adăugăm listener pentru focus pe elementele focusabile
    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, [settings.textToSpeech, speakText]);

  const value: AccessibilityContextType = {
    settings,
    updateSetting,
    resetSettings,
    isAccessibilityPanelOpen,
    toggleAccessibilityPanel,
    speakText,
    stopSpeaking,
    isSpeaking,
    currentSpeakingElement,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}; 